import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Bloco de atenção — componente 11.
 * Fio de 2px à esquerda, no topo do conteúdo, resumindo o que exige decisão antes de agir.
 * `plum`: risco clínico ou pendência ("3 respostas pedem atenção antes do procedimento").
 * `lilac`: contexto sem risco ("Apareceu porque você respondeu sim").
 */
export interface AttentionBlockProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: 'plum' | 'lilac'
  title?: ReactNode
  /** Fundo: `surface` (padrão, sobre pampas) ou `pampas` (dentro de um cartão branco). */
  tone?: 'surface' | 'pampas'
  children: ReactNode
}

export function AttentionBlock({
  variant = 'plum',
  tone = 'surface',
  title,
  children,
  className,
  ...props
}: AttentionBlockProps) {
  return (
    <div
      data-slot="attention-block"
      data-variant={variant}
      role={variant === 'plum' ? 'note' : undefined}
      className={cn(
        'border-l-2 px-[18px] py-4',
        variant === 'plum' ? 'border-plum' : 'border-lilac',
        tone === 'surface' ? 'bg-surface' : 'bg-pampas',
        className,
      )}
      {...props}
    >
      {title ? (
        <div className="font-display text-ink text-[13px] leading-[1.3] font-normal">{title}</div>
      ) : null}
      <div className={cn('font-body text-body text-ink leading-[1.8] font-light', title && 'mt-2')}>
        {children}
      </div>
    </div>
  )
}
